import {
    Button,
    Box,
    Typography,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    // Card, CardContent removed - not used
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Visibility } from '@mui/icons-material';
import { CheckCircleOutline, Warning, ArrowUpward, Circle, ArrowDownward } from '@mui/icons-material';
import PastDueLogo from '../../../../assets/images/pastDueLogo.png';
import { NavLink, useHistory } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getCategorizedToDos, DeleteToDo } from 'core/API/todo';
import { useWorkplaceUser } from 'module/documents/utils';
// TodoWordIcon removed - not used
import { useEffect, useState } from 'react';
import { useDocumentContext } from 'module/documents/context';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
// Edit icon not used here
import styles from './Todo.module.scss';
import {
    safeDateParse,
    formatDateMMDDYYYY,
    formatDateWithTime,
    formatDateWith12HourTime,
    formatDateTime12Hour,
    // validateDateNotPast,
    // isSameDay,
    getTodayISO,
} from 'shared/utils/dateUtils';
import { getBroadcastMessage, getBroadcastOutbox, markBroadcastAsRead } from 'core/API/broadcast';

const Todo = () => {
    const {
        selectedEmployee: _selectedEmployee,
        startDate,
        setStartDate,
        endDate: _endDate,
        setEndDate: _setEndDate,
    } = useDocumentContext();
    const workplace = useWorkplaceUser();
    const workplaceId = Number(localStorage.getItem('workplace-id')) || workplace.data.id;

    const queryClient = useQueryClient();
    const { showToast } = useToastContext();
    const { push } = useHistory();

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [todoToDelete, setTodoToDelete] = useState<any>(null);

    // Query for inbox (received messages)
    const {
        data: broadcastData,
        isLoading,
        error,
    } = useQuery(['broadcast', workplaceId], () => getBroadcastMessage({ workplaceId, page: 1, itemsPerPage: 10 }), {
        enabled: !!workplaceId,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    // Query for outbox (sent messages)
    const {
        data: outboxData,
        isLoading: isOutboxLoading,
        error: outboxError,
    } = useQuery(['broadcast-outbox', workplaceId], () => getBroadcastOutbox({ workplaceId, page: 1, itemsPerPage: 10 }), {
        enabled: !!workplaceId,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    // Mutation to mark broadcast as read
    const { mutate: markAsRead } = useMutation(markBroadcastAsRead, {
        onSuccess: () => {
            // Optionally refetch the data to update read status
            queryClient.invalidateQueries(['broadcast', workplaceId]);
        },
        onError: (error) => {
            console.error('Failed to mark broadcast as read:', error);
        },
    });

    // Handler for eye button click - only call API for My Inbox (unread messages)
    const handleViewClick = (messageId: number, e: React.MouseEvent, shouldMarkAsRead: boolean = false) => {
        e.preventDefault();
        // Only call markAsRead API for My Inbox (unread messages)
        if (shouldMarkAsRead) {
            markAsRead(messageId);
        }
        // Navigate to the view-only page
        push(ROUTES.VIEW_BROADCAST.replace(':id', messageId.toString()));
    };

    // console.log('mytodos', myTodoList, todoList);
    // Permission checks for To-Do module (call hooks before any early returns)
    const {
        hasModuleViewPermission,
        hasModuleEditPermission,
        hasModuleDeletePermission,
    } = usePermissionCheck();
    const canViewToDoMy = hasModuleViewPermission('To-Do - My');
    const canViewToDoAll = hasModuleViewPermission('To-Do - All');

    // Broadcast permission checks (module-level, like Logbook)
    const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
    const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
    const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');
    const canEditBroadcastMy = hasModuleEditPermission('Broadcast - My');
    const canEditBroadcastAll = hasModuleEditPermission('Broadcast - All');
    const canEditBroadcastOrganization = hasModuleEditPermission('Broadcast - Organization');
    const canDeleteBroadcastMy = hasModuleDeletePermission('Broadcast - My');
    const canDeleteBroadcastAll = hasModuleDeletePermission('Broadcast - All');
    const canDeleteBroadcastOrganization = hasModuleDeletePermission('Broadcast - Organization');
    // Determine edit capability:
    // If user can view 'To-Do - All' allow edit when they have edit on either label
    // If user can only view 'To-Do - My' allow edit only when they have edit on 'To-Do - My'
    let canEditToDo = false;
    if (canViewToDoAll) {
        canEditToDo = hasModuleEditPermission('To-Do - All') || hasModuleEditPermission('To-Do - My');
    } else if (canViewToDoMy) {
        canEditToDo = hasModuleEditPermission('To-Do - My');
    } else {
        canEditToDo = false;
    }

    // Determine delete capability similarly to edit capability
    let canDeleteToDo = false;
    if (canViewToDoAll) {
        canDeleteToDo = hasModuleDeletePermission('To-Do - All') || hasModuleDeletePermission('To-Do - My');
    } else if (canViewToDoMy) {
        canDeleteToDo = hasModuleDeletePermission('To-Do - My');
    } else {
        canDeleteToDo = false;
    }

    // Delete todo mutation
    const { mutate: deleteTodo, isLoading: isDeleting } = useMutation(
        (id: number) => DeleteToDo(id),
        {
            onSuccess: () => {
                // Refetch todos after successful deletion
                queryClient.invalidateQueries(['todos-categorized', workplaceId]);
                setDeleteDialogOpen(false);
                setTodoToDelete(null);
                showToast('success', 'Todo deleted successfully!');
            },
            onError: (error) => {
                console.error('Failed to delete todo:', error);
                setDeleteDialogOpen(false);
                setTodoToDelete(null);
                showToast('error', 'Failed to delete todo. Please try again.');
            },
        }
    );

    useEffect(() => {
        if (!startDate) {
            const today = getTodayISO();
            setStartDate(today);
        }
    }, [startDate, setStartDate]);

    if (isLoading || isOutboxLoading) {
        return (
            <Box className={styles.loadingState}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) return <div>Error fetching Broadcast</div>;
    if (outboxError) return <div>Error fetching Outbox</div>;

    // Extract data from categorized API response (arrays are directly on root object)
    const myInbox = Array.isArray(broadcastData?.member) ? broadcastData.member : [];
    const myOutbox = Array.isArray(outboxData?.member) ? outboxData.member : [];
    const outstandingTodos = Array.isArray(broadcastData?.outstanding_todos) ? broadcastData.outstanding_todos : [];
    const pastDueTodos = Array.isArray(broadcastData?.past_due_todos) ? broadcastData.past_due_todos : [];
    const completedTodos = Array.isArray(broadcastData?.completed_todos) ? broadcastData.completed_todos : [];

    const getUserFullName = (user: any): string => {
        // Handle null or undefined user
        if (!user) {
            return 'Unknown User';
        }
        const parts = [user?.firstName, user?.middleName, user?.lastName].filter(
            (p: string | undefined) => !!p && p.trim() !== ''
        );
        return parts.join(' ') || 'Unknown User';
    };

    // parseApiPathId removed - not needed

    // Helper function to format date with time as MM/DD/YYYY HH:MM:SS AM/PM
    const formatTodoDateWithTime = (todo: any): string => {
        const dueDateRaw = todo.due_date ?? todo.dueDate;
        const dueTime = todo.dueTime;
        return formatDateTime12Hour(dueDateRaw, dueTime);
    };

    // Helper function to get assignment display value
    const getAssignmentDisplay = (todo: any): string => {
        // Check if user exists and is not null
        if (todo?.user && todo.user !== null) {
            return getUserFullName(todo.user);
        } else if (todo?.assignedGroup) {
            return todo.assignedGroup.label || 'Group Assignment';
        } else if (todo?.assigned) {
            return todo.assigned;
        } else {
            return 'Unassigned';
        }
    };

    // Helper to get a robust description across varying API shapes
    const getDescriptionDisplay = (todo: any): string => {
        const candidates = [
            todo?.description,
            todo?.label,
            todo?.name,
            todo?.title,
            todo?.note?.message,
            todo?.note?.description,
            typeof todo?.note === 'string' ? todo?.note : undefined,
        ];
        const val = candidates.find((v: any) => typeof v === 'string' && v.trim() !== '');
        return (val as string) || 'N/A';
    };

    // Delete handlers
    const handleDeleteClick = (todo: any) => {
        setTodoToDelete(todo);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (todoToDelete) {
            deleteTodo(todoToDelete.id);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setTodoToDelete(null);
    };

    // Helper function to get priority symbol, name, and color
    const getPriorityIcon = (priorityLabel: string | null | undefined) => {
        const label = (priorityLabel || '').toLowerCase();
        if (label === 'critical' || label === 'urgent') {
            return { 
                icon: <Warning sx={{ color: '#d32f2f', fontSize: '20px' }} />, 
                name: 'Urgent',
                color: '#d32f2f',
                order: 1 
            };
        } else if (label === 'high') {
            return { 
                icon: <ArrowUpward sx={{ color: '#f57c00', fontSize: '20px' }} />, 
                name: 'High',
                color: '#f57c00',
                order: 2 
            };
        } else if (label === 'medium') {
            return { 
                icon: <Circle sx={{ color: '#388e3c', fontSize: '20px' }} />, 
                name: 'Medium',
                color: '#388e3c',
                order: 3 
            };
        } else if (label === 'low') {
            return { 
                icon: <ArrowDownward sx={{ color: '#1976d2', fontSize: '20px' }} />, 
                name: 'Low',
                color: '#1976d2',
                order: 4 
            };
        }
        return { 
            icon: <Circle sx={{ color: '#9e9e9e', fontSize: '20px' }} />, 
            name: priorityLabel || 'N/A',
            color: '#9e9e9e',
            order: 5 
        };
    };

    // Helper function to get receiver name (user or group)
    const getReceiverName = (message: any): string => {
        if (message.receiverUser) {
            return getUserFullName(message.receiverUser);
        } else if (message.receiverGroup?.groupName) {
            return message.receiverGroup.groupName;
        }
        return 'Unknown User';
    };

    // Helper function to format date from createdDate field (format to MM/DD/YYYY)
    const formatDateFromAPI = (createdDate: string | null | undefined): string => {
        if (!createdDate) return 'N/A';
        // Use formatDateToMMDDYYYY to convert to MM/DD/YYYY format
        const formatted = formatDateMMDDYYYY(createdDate);
        return formatted !== 'N/A' ? formatted : 'N/A';
    };

    // Helper function to format time from createdTime field (HH:MM:SS AM/PM)
    const formatTimeFromAPI = (createdTime: string | null | undefined): string => {
        if (!createdTime) return 'N/A';
        
        // Parse time string (could be HH:MM:SS or HH:MM format)
        const timeParts = createdTime.split(':');
        if (timeParts.length >= 2) {
            const hour = Number(timeParts[0]) || 0;
            const minute = Number(timeParts[1]) || 0;
            const second = timeParts.length >= 3 ? (Number(timeParts[2]) || 0) : 0;
            
            // Convert to 12-hour format
            let hours12 = hour;
            const minutesStr = String(minute).padStart(2, '0');
            const secondsStr = String(second).padStart(2, '0');
            const ampm = hours12 >= 12 ? 'PM' : 'AM';
            hours12 = hours12 % 12;
            hours12 = hours12 ? hours12 : 12; // the hour '0' should be '12'
            const hoursStr = String(hours12).padStart(2, '0');
            
            return `${hoursStr}:${minutesStr}:${secondsStr} ${ampm}`;
        }
        
        return createdTime;
    };

    // Filter broadcasts based on permissions (similar to Schedule filtering)
    // Get current user ID for filtering
    const currentUserIdStr = localStorage.getItem('userId');
    const currentUserId = currentUserIdStr ? Number(currentUserIdStr) : null;

    // Filter broadcasts based on permissions
    let filteredInbox: any[] = [];
    let filteredOutbox: any[] = [];

    const getReceiverUserId = (message: any) => {
        return message.receiverUser?.id ||
            (typeof message.receiverUser === 'string'
                ? parseInt(message.receiverUser.split('/').pop() || '0')
                : null);
    };

    const getSenderUserId = (message: any) => {
        return message.sender?.id ||
            (typeof message.sender === 'string'
                ? parseInt(message.sender.split('/').pop() || '0')
                : null);
    };

    const isOrganizationBroadcast = (message: any) =>
        message.receiverGroup?.organization_broadcast === true ||
        message.organization_broadcast === true ||
        (message.receiverGroup?.groupName && String(message.receiverGroup.groupName).toLowerCase().includes('entire organization'));

    const getDisplayCreatedFields = (message: any): { createdDate?: string; createdTime?: string } => {
        const useEntireOrgFields = isOrganizationBroadcast(message);
        return {
            createdDate: useEntireOrgFields ? (message.entireOrgCreatedDate ?? message.createdDate) : message.createdDate,
            createdTime: useEntireOrgFields ? (message.entireOrgCreatedTime ?? message.createdTime) : message.createdTime,
        };
    };

    const filterMyInbox = () => {
        if (!currentUserId) return [];
        return myInbox.filter((message: any) => getReceiverUserId(message) === currentUserId);
    };

    const filterMyOutbox = () => {
        if (!currentUserId) return [];
        return myOutbox.filter((message: any) => getSenderUserId(message) === currentUserId);
    };

    const filterOrgInbox = () => myInbox.filter((message: any) => isOrganizationBroadcast(message));
    const filterOrgOutbox = () => myOutbox.filter((message: any) => isOrganizationBroadcast(message));

    const mergeUnique = (items: any[]) => {
        const seen = new Set();
        return items.filter((item: any) => {
            const key = item?.id ?? JSON.stringify(item);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    if (canViewBroadcastAll) {
        // Show all inbox/outbox but hide "Entire Organization" broadcasts if user has no Broadcast - Organization view permission
        filteredInbox = canViewBroadcastOrganization ? myInbox : myInbox.filter((m: any) => !isOrganizationBroadcast(m));
        filteredOutbox = canViewBroadcastOrganization ? myOutbox : myOutbox.filter((m: any) => !isOrganizationBroadcast(m));
    } else {
        const inboxBuckets: any[] = [];
        const outboxBuckets: any[] = [];

        if (canViewBroadcastMy) {
            inboxBuckets.push(...filterMyInbox());
            outboxBuckets.push(...filterMyOutbox());
        }

        if (canViewBroadcastOrganization) {
            inboxBuckets.push(...filterOrgInbox());
            outboxBuckets.push(...filterOrgOutbox());
        }

        filteredInbox = mergeUnique(inboxBuckets);
        filteredOutbox = mergeUnique(outboxBuckets);
    }

    const getMessageDateTime = (message: any): Date | null => {
        // Prefer full timestamp if available (API may use createdAt or created_at)
        const isoField = message.createdAt ?? message.created_at;
        if (isoField) {
            const isoDate = safeDateParse(isoField);
            if (isoDate) return isoDate;
        }

        // Fallback to createdDate + createdTime fields
        // For organization broadcasts, API may provide entireOrgCreatedDate/entireOrgCreatedTime.
        const { createdDate, createdTime } = getDisplayCreatedFields(message);
        if (!createdDate) return null;

        const baseDate = safeDateParse(createdDate);
        if (!baseDate) return null;

        if (!createdTime) return baseDate;

        const timeParts = String(createdTime).trim().split(/\s+/); // ["06:15:00", "AM"]
        const clock = timeParts[0] || '';
        const ampm = (timeParts[1] || '').toUpperCase();

        const [hStr, mStr, sStr] = clock.split(':');
        let hours = Number(hStr) || 0;
        const minutes = Number(mStr) || 0;
        const seconds = Number(sStr) || 0;

        // Convert 12h clock to 24h
        if (ampm === 'PM' && hours < 12) {
            hours += 12;
        } else if (ampm === 'AM' && hours === 12) {
            hours = 0;
        }

        const date = new Date(baseDate);
        date.setHours(hours, minutes, seconds, 0);
        return isNaN(date.getTime()) ? null : date;
    };

    // Sorting function for Inbox: Priority first (Urgent > High > Medium > Low), then by date/time (most recent first)
    const sortMessagesByPriority = (messages: any[]) => {
        return [...messages].sort((a, b) => {
            const priorityA = getPriorityIcon(a.priority?.label || a.priority?.name);
            const priorityB = getPriorityIcon(b.priority?.label || b.priority?.name);
            
            // Sort by priority first
            if (priorityA.order !== priorityB.order) {
                return priorityA.order - priorityB.order;
            }
            
            // Then sort by date/time (most recent first)
            const dateA = getMessageDateTime(a);
            const dateB = getMessageDateTime(b);
            if (dateA && dateB) {
                return dateB.getTime() - dateA.getTime(); // Most recent first
            }
            return 0;
        });
    };

    // Sorting function for Archive: Date/time only (most recent first). No priority.
    const sortMessagesByDate = (messages: any[]) => {
        return [...messages].sort((a, b) => {
            const dateA = getMessageDateTime(a);
            const dateB = getMessageDateTime(b);
            if (dateA && dateB) {
                return dateB.getTime() - dateA.getTime(); // Most recent first
            }
            // Put items without date at the end; between them sort by id desc so API priority order is not kept
            if (dateA && !dateB) return -1;
            if (!dateA && dateB) return 1;
            return (b.id ?? 0) - (a.id ?? 0);
        });
    };

    // Sorting function for Broadcast Outbox only: use created_at/createdAt when available for precise time,
    // otherwise fall back to getMessageDateTime. Most recent first.
    const sortOutboxMessagesByDate = (messages: any[]) => {
        const getOutboxTimestamp = (message: any): number => {
            const iso = message.created_at ?? message.createdAt;
            if (iso) {
                const d = new Date(iso);
                if (!Number.isNaN(d.getTime())) {
                    return d.getTime();
                }
            }

            const dt = getMessageDateTime(message);
            return dt ? dt.getTime() : 0;
        };

        return [...messages].sort((a, b) => getOutboxTimestamp(b) - getOutboxTimestamp(a));
    };

    // Filter todos based on permissions (security checks remain)
    // My Inbox: show only unread messages (isRead === false) - sorted by priority, then date/time
    const outstandingMine = sortMessagesByPriority(filteredInbox.filter((message: any) => message.isRead === false));
    
    // My Archive: show only read messages (isRead === true) - sorted by date/time only (most recent first)
    const archiveMessages = sortMessagesByDate(filteredInbox.filter((message: any) => message.isRead === true));
    
    // My Outbox: sorted by created_at / createdAt (most recent first, including time)
    const sortedOutbox = sortOutboxMessagesByDate(filteredOutbox);

    // All Outstanding: show outstanding_todos from API, filtered by permissions
    const allOutstandingToShow = canViewToDoAll
        ? outstandingTodos
        : canViewToDoMy
            ? outstandingMine
            : [];

    // Completed: show completed_todos from API, filtered by permissions
    const completedToShow = canViewToDoAll
        ? completedTodos
        : canViewToDoMy
            ? completedTodos.filter((todo: any) => {
                    const userId = todo.user?.id;
                    return userId === currentUserId;
                })
            : [];

    // Past Due: show past_due_todos from API, filtered by permissions
    const overdueToShow = canViewToDoAll
        ? pastDueTodos
        : canViewToDoMy
            ? pastDueTodos.filter((todo: any) => {
                    const userId = todo.user?.id;
                    return userId === currentUserId;
                })
            : [];

    // Function to render "No Data" message
    const renderNoData = () => (
        <Box className={styles.noRecordsFound}>
            <Typography variant="h6" color="textSecondary">
                No records found
            </Typography>
        </Box>
    );

    return (
        <Box p={2} sx={{ backgroundColor: '#f0f6fc', minHeight: '100vh' }}>
            {/* Header */}
            <Box display="flex" justifyContent="end" alignItems="center" mb={2}>
                {(canEditBroadcastMy || canEditBroadcastAll || canEditBroadcastOrganization) && (
                    <Button
                        component={NavLink}
                        to={ROUTES.ADD_BROADCAST}
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 15px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                            textDecoration: 'none',
                        }}
                    >
                        Send
                    </Button>
                )}
            </Box>

            {/* Outstanding - Mine */}
            <Typography variant="h6" bgcolor={'#fff'} padding={'10px'} gutterBottom>
                My Inbox
            </Typography>
            <TableContainer
                style={{ height: 'max-content', maxHeight: '300px' }}
                component={Paper}
                sx={{ mb: 2 }}
            >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                        <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ width: '10%' }}>
                                <b>Priority</b>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <b>From</b>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <b>To</b>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <b>Subject</b>
                            </TableCell>
                            <TableCell sx={{ width: '12%' }}>
                                <b>Date</b>
                            </TableCell>
                            <TableCell sx={{ width: '12%' }}>
                                <b>Time</b>
                            </TableCell>
                            <TableCell sx={{ width: '8%' }}>
                                <b>Read</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!(canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization) ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    You do not have permissions to view this!
                                </TableCell>
                            </TableRow>
                        ) : outstandingMine.length > 0 ? (
                            outstandingMine.map((todo: any) => {
                                const priorityIcon = getPriorityIcon(todo.priority?.label || todo.priority?.name);
                                return(
                                <TableRow 
                                    key={todo.id}
                                    onClick={(e) => handleViewClick(todo.id, e, true)}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: '#f5f5f5' }
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold', padding: '8px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {priorityIcon.icon}
                                            <Typography sx={{ color: priorityIcon.color, fontSize: '14px', fontWeight: 'bold' }}>
                                                {priorityIcon.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{getUserFullName(todo.sender)}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{getReceiverName(todo)}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{todo.title ?? todo.title}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{formatDateFromAPI(getDisplayCreatedFields(todo).createdDate)}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{formatTimeFromAPI(getDisplayCreatedFields(todo).createdTime)}</TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <IconButton 
                                            sx={{ color: 'rgb(68, 99, 136)' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewClick(todo.id, e, true);
                                            }}
                                        >
                                            <Visibility style={{ fontSize: '20px' }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7}>{renderNoData()}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* My Outbox */}
            <Typography variant="h6" bgcolor={'#fff'} padding={'10px'} gutterBottom>
                My Outbox
            </Typography>
            <TableContainer
                style={{ height: 'max-content', maxHeight: '300px' }}
                component={Paper}
                sx={{ mb: 2 }}
            >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                        <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ width: '10%' }}>
                                <b>Priority</b>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <b>From</b>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <b>To</b>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <b>Subject</b>
                            </TableCell>
                            <TableCell sx={{ width: '12%' }}>
                                <b>Date</b>
                            </TableCell>
                            <TableCell sx={{ width: '12%' }}>
                                <b>Time</b>
                            </TableCell>
                            <TableCell sx={{ width: '8%' }}>
                                <b>Read</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!(canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization) ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    You do not have permissions to view this!
                                </TableCell>
                            </TableRow>
                        ) : sortedOutbox.length > 0 ? (
                            sortedOutbox.map((message: any) => {
                                const priorityIcon = getPriorityIcon(message.priority?.label || message.priority?.name);
                                return (
                                    <TableRow 
                                        key={message.id}
                                        onClick={(e) => handleViewClick(message.id, e, false)}
                                        sx={{ 
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#f5f5f5' }
                                        }}
                                    >
                                        <TableCell sx={{ padding: '8px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {priorityIcon.icon}
                                                <Typography sx={{ color: priorityIcon.color, fontSize: '14px' }}>
                                                    {priorityIcon.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {getUserFullName(message.sender)}
                                        </TableCell>
                                        <TableCell>{getReceiverName(message)}</TableCell>
                                        <TableCell>{message.title ?? 'N/A'}</TableCell>
                                        <TableCell>{formatDateFromAPI(getDisplayCreatedFields(message).createdDate)}</TableCell>
                                        <TableCell>{formatTimeFromAPI(getDisplayCreatedFields(message).createdTime)}</TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <IconButton 
                                                sx={{ color: 'rgb(68, 99, 136)' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewClick(message.id, e, false);
                                                }}
                                            >
                                                <Visibility style={{ fontSize: '20px' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7}>{renderNoData()}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Completed and Overdue Tables */}
                {/* Completed To-Do Table */}
                <Typography variant="h6" bgcolor={'#fff'} padding={'10px'} gutterBottom>
                    My Archive
                </Typography>
                <TableContainer
                    style={{ height: 'max-content', maxHeight: '300px' }}
                    component={Paper}
                    sx={{ mb: 2 }}
                >
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ width: '10%' }}>
                                    <b>Priority</b>
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>
                                    <b>From</b>
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>
                                    <b>To</b>
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>
                                    <b>Subject</b>
                                </TableCell>
                                <TableCell sx={{ width: '12%' }}>
                                    <b>Date</b>
                                </TableCell>
                                <TableCell sx={{ width: '12%' }}>
                                    <b>Time</b>
                                </TableCell>
                                <TableCell sx={{ width: '8%' }}>
                                    <b>Read</b>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!(canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization) ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        You do not have permissions to view this!
                                    </TableCell>
                                </TableRow>
                            ) : archiveMessages.length > 0 ? (
                                archiveMessages.map((message: any) => {
                                    const priorityIcon = getPriorityIcon(message.priority?.label || message.priority?.name);
                                    return (
                                        <TableRow 
                                            key={message.id}
                                            onClick={(e) => handleViewClick(message.id, e, false)}
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: '#f5f5f5' }
                                            }}
                                        >
                                            <TableCell sx={{ padding: '8px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {priorityIcon.icon}
                                                    <Typography sx={{ color: priorityIcon.color, fontSize: '14px' }}>
                                                        {priorityIcon.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{getUserFullName(message.sender)}</TableCell>
                                            <TableCell>{getReceiverName(message)}</TableCell>
                                            <TableCell>{message.title ?? 'N/A'}</TableCell>
                                            <TableCell>{formatDateFromAPI(getDisplayCreatedFields(message).createdDate)}</TableCell>
                                            <TableCell>{formatTimeFromAPI(getDisplayCreatedFields(message).createdTime)}</TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <IconButton 
                                                    sx={{ color: 'rgb(68, 99, 136)' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewClick(message.id, e, false);
                                                    }}
                                                >
                                                    <Visibility style={{ fontSize: '20px' }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7}>{renderNoData()}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the todo &ldquo;{todoToDelete?.description}&rdquo;? This
                        action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} style={{ color: 'black' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        style={{ backgroundColor: 'red', color: 'white' }}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Todo;
